import { Feature, Point, Polygon, MultiPolygon, Position } from "geojson";
import type { Job } from "../../../models";
import { POLYGON_COLORS } from "./JobMap.types";

/**
 * Get a random color from the polygon colors palette
 */
export function getRandomPolygonColor(): string {
	const index = Math.floor(Math.random() * POLYGON_COLORS.length);
	return POLYGON_COLORS[index];
}

/**
 * Get a consistent color for a job based on its ID.
 * Uses a simple hash function to map the job ID to a stable color index.
 */
export function getJobColor(jobId: string): string {
	let hash = 0;
	for (let i = 0; i < jobId.length; i++) {
		const char = jobId.charCodeAt(i);
		hash = (hash << 5) - hash + char;
		hash = hash & hash; // Convert to 32-bit integer
	}
	const index = Math.abs(hash) % POLYGON_COLORS.length;
	return POLYGON_COLORS[index];
}

/**
 * Safely extract a property from a GeoJSON feature
 */
export function getShapeFeatureProperty(feature: Feature<Polygon | Point>, key: string): string {
	return feature.properties && key in feature.properties ? feature.properties[key] : "";
}

/**
 * Parse the change value from feature properties
 */
export function parseChangeInfo(change: string): {
	hasChange: boolean;
	changeType: string;
	changeReason: string;
} {
	const [changeType, changeReason] = change.includes(":") ? change.split(":") : ["", ""];
	const hasChange = Boolean(changeType && changeType !== "None");
	return { hasChange, changeType, changeReason: changeReason || "" };
}

/**
 * Check if a marker position is visible within the current bounds and zoom level
 */
export function isMarkerVisible(
	position: google.maps.LatLngLiteral,
	bounds: google.maps.LatLngBounds,
	zoom: number | undefined,
	zoomThreshold: number | undefined,
): boolean {
	// When no zoom threshold is set, show points at any zoom but still filter by bounds
	if (zoomThreshold !== undefined && (!zoom || zoom < zoomThreshold)) {
		return false;
	}
	return bounds.contains(new google.maps.LatLng(position.lat, position.lng));
}

// ── Boundary helpers (merged from IssueMap) ─────────────────────────────────

/**
 * Gets the boundary feature from a job, checking all possible locations
 */
export function getJobBoundaryGeometry(job: Job): Polygon | MultiPolygon | null {
	// Check files.boundaryGeoJSON first (primary location)
	const boundaryFromFiles = job.files?.boundaryGeoJSON;
	if (boundaryFromFiles?.features?.[0]?.geometry) {
		const geom = boundaryFromFiles.features[0].geometry;
		if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
			return geom as Polygon | MultiPolygon;
		}
	}

	// Check fileOptions.boundaryGeoJSON (alternative location)
	const boundaryFromFileOptions = job.fileOptions?.boundaryGeoJSON;
	if (boundaryFromFileOptions?.features?.[0]?.geometry) {
		const geom = boundaryFromFileOptions.features[0].geometry;
		if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
			return geom as Polygon | MultiPolygon;
		}
	}

	return null;
}

/**
 * Convert GeoJSON coordinates to Google Maps LatLngLiteral array
 */
export function convertCoordinatesToPath(coordinates: Position[]): google.maps.LatLngLiteral[] {
	return coordinates.map((coord) => ({
		lat: coord[1],
		lng: coord[0],
	}));
}

/**
 * Extract paths from a GeoJSON Polygon or MultiPolygon geometry
 */
export function extractPathsFromGeometry(geometry: Polygon | MultiPolygon): google.maps.LatLngLiteral[][] {
	if (geometry.type === "Polygon") {
		return [convertCoordinatesToPath(geometry.coordinates[0])];
	} else if (geometry.type === "MultiPolygon") {
		return geometry.coordinates.map((polygon) => convertCoordinatesToPath(polygon[0]));
	}
	return [];
}

/**
 * Get all coordinates from a geometry for bounds calculation
 */
export function getAllCoordinatesFromGeometry(geometry: Polygon | MultiPolygon): google.maps.LatLngLiteral[] {
	const coords: google.maps.LatLngLiteral[] = [];
	if (geometry.type === "Polygon") {
		geometry.coordinates[0].forEach((coord) => {
			coords.push({ lat: coord[1], lng: coord[0] });
		});
	} else if (geometry.type === "MultiPolygon") {
		geometry.coordinates.forEach((polygon) => {
			polygon[0].forEach((coord) => {
				coords.push({ lat: coord[1], lng: coord[0] });
			});
		});
	}
	return coords;
}

/**
 * Generate parallel hatch lines clipped to a polygon or multipolygon.
 * Returns an array of line segments as LatLngLiteral arrays.
 * Uses dynamic import of @turf/turf to avoid bundle impact on non-wizard consumers.
 */
export async function generateHatchLines(
	geometry: Polygon | MultiPolygon,
	angleDegrees: number,
	spacingMeters: number,
): Promise<google.maps.LatLngLiteral[][]> {
	const turf = await import("@turf/turf");

	const feature = turf.feature(geometry);
	const bbox = turf.bbox(feature);
	const bboxCenter = turf.center(feature);
	const diagonal = turf.distance(turf.point([bbox[0], bbox[1]]), turf.point([bbox[2], bbox[3]]), {
		units: "meters",
	});

	if (diagonal < spacingMeters) return [];

	const halfDiag = diagonal / 2;
	const results: google.maps.LatLngLiteral[][] = [];

	for (let offset = -halfDiag; offset <= halfDiag; offset += spacingMeters) {
		const perpAngle = angleDegrees + 90;
		const offsetPoint = turf.destination(bboxCenter, offset / 1000, perpAngle, { units: "kilometers" });

		const lineStart = turf.destination(offsetPoint, halfDiag / 1000, angleDegrees, { units: "kilometers" });
		const lineEnd = turf.destination(offsetPoint, halfDiag / 1000, angleDegrees + 180, { units: "kilometers" });

		const line = turf.lineString([turf.getCoord(lineStart), turf.getCoord(lineEnd)]);

		try {
			const split = turf.lineSplit(line, feature);

			for (const segment of split.features) {
				const coords = segment.geometry.coordinates;
				const mid = turf.midpoint(turf.point(coords[0]), turf.point(coords[coords.length - 1]));
				if (turf.booleanPointInPolygon(mid, feature)) {
					results.push(coords.map(([lng, lat]) => ({ lat, lng })));
				}
			}
		} catch {
			continue;
		}
	}

	return results;
}
